import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from "react"
import { useAuth } from "@/lib/auth-context"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Plus, Printer, Pencil, Home, Tag, Egg } from "lucide-react"
import { formatBRDate, todayISODate } from "@/lib/br-dates"
import { MortalidadeAvulsoForm } from "@/components/mortalidades/mortalidade-avulso-form"
import { mortalidadeErrorMessage, parseApiDetail } from "@/lib/mortalidades"
import { exigeMotivo } from "@/lib/coletas"
import { API_BASE } from '@/lib/api-base'

interface Coleta {
  id: string
  codigo_coleta: string
  galpao_id: string
  lote_id: string
  data: string
  hora: string
  status_classificacao: string
  total_ovos: number
  ovos_quebrados_na_coleta: number
  versao: number
  criado_por_nome?: string | null
  alterado_em: string | null
  alterado_por_nome: string | null
  motivo_alteracao: string | null
  idade_minutos?: number | null
}

interface Galpao {
  id: string
  nome: string
  status: string
}

interface Lote {
  id: string
  codigo: string
  galpao_id: string
  status: string
}

function numberFieldValue(n: number): number | "" {
  return Number.isFinite(n) && n > 0 ? n : ""
}

function parseCount(raw: string): number {
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
}

function toTimeInputValue(hora: string): string {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(hora ?? ""))
  if (!match) return "00:00"
  const hours = Number.parseInt(match[1], 10)
  const minutes = Number.parseInt(match[2], 10)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return "00:00"
  const hh = Math.min(23, Math.max(0, hours))
  const mm = Math.min(59, Math.max(0, minutes))
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`
}

function toDateInputValue(data: string): string {
  const day = String(data ?? "").slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : ""
}

type ColetaFormState = {
  galpao_id: string
  lote_id: string
  data: string
  hora: string
  total_ovos: number
  ovos_quebrados_na_coleta: number
  mortalidade_quantidade: number
  mortalidade_causa: string
}

function defaultColetaForm(): ColetaFormState {
  return {
    galpao_id: "",
    lote_id: "",
    data: todayISODate(),
    hora: "00:00",
    total_ovos: 0,
    ovos_quebrados_na_coleta: 0,
    mortalidade_quantidade: 0,
    mortalidade_causa: "",
  }
}

function ColetasPage() {
  const apiBase = API_BASE
  const { user, apiFetch, isSuperAdmin, selectedTenantId } = useAuth()
  const [coletas, setColetas] = useState<Coleta[]>([])
  const [galpoes, setGalpoes] = useState<Galpao[]>([])
  const [lotes, setLotes] = useState<Lote[]>([])
  const [showForm, setShowForm] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [lastCreatedColeta, setLastCreatedColeta] = useState<Coleta | null>(null)
  const [editColeta, setEditColeta] = useState<Coleta | null>(null)
  const [editForm, setEditForm] = useState({ total_ovos: 0, ovos_quebrados_na_coleta: 0, motivo_alteracao: "" })
  const [editError, setEditError] = useState<string | null>(null)
  const [pendentes, setPendentes] = useState<Coleta[]>([])
  const [showPendentes, setShowPendentes] = useState(false)
  const [pendentesMais24h, setPendentesMais24h] = useState(0)
  const [printError, setPrintError] = useState<string | null>(null)
  const [printingId, setPrintingId] = useState<string | null>(null)
  const [mortalidadePostError, setMortalidadePostError] = useState<string | null>(null)
  const [showRetryMortalidade, setShowRetryMortalidade] = useState(false)
  const [failedMortalidadeDraft, setFailedMortalidadeDraft] = useState<{ quantidade: number; causa: string } | null>(null)
  const [form, setForm] = useState<ColetaFormState>(defaultColetaForm)

  const fetchColetas = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/coletas/`)
      if (res.ok) {
        setColetas(await res.json())
      }
    } catch (e) {
      console.error(e)
      setLoadError("Não foi possível carregar os dados. Tente novamente.")
    }
  }, [apiFetch, apiBase])

  const fetchPendentes = async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/coletas/pendentes`)
      if (res.ok) {
        setPendentes(await res.json())
      }
    } catch (e) {
      console.error(e)
      setLoadError("Não foi possível carregar os dados. Tente novamente.")
    }
  }

  const fetchAlertasPendentesVelhos = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/coletas/pendentes/alertas-velhos?minutos=1440`)
      if (res.ok) {
        const data = await res.json() as { quantidade: number }
        setPendentesMais24h(data.quantidade ?? 0)
      }
    } catch (e) {
      console.error(e)
      setLoadError("Não foi possível carregar os dados. Tente novamente.")
    }
  }, [apiFetch, apiBase])

  const fetchGalpoes = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/galpoes/`)
      if (res.ok) {
        setGalpoes((await res.json()).filter((g: Galpao) => g.status === "ativo"))
      }
    } catch (e) {
      console.error(e)
      setLoadError("Não foi possível carregar os dados. Tente novamente.")
    }
  }, [apiFetch, apiBase])

  const fetchLotes = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/lotes/`)
      if (res.ok) {
        setLotes((await res.json()).filter((l: Lote) => l.status === "ativo"))
      }
    } catch (e) {
      console.error(e)
      setLoadError("Não foi possível carregar os dados. Tente novamente.")
    }
  }, [apiFetch, apiBase])

  const retryLoad = () => {
    setLoadError(null)
    void fetchColetas()
    void fetchGalpoes()
    void fetchLotes()
    void fetchAlertasPendentesVelhos()
  }

  useEffect(() => {
    if (!user) return
    if (isSuperAdmin && !selectedTenantId) return
    void fetchColetas()
    void fetchGalpoes()
    void fetchLotes()
    void fetchAlertasPendentesVelhos()
  }, [user, isSuperAdmin, selectedTenantId, fetchColetas, fetchGalpoes, fetchLotes, fetchAlertasPendentesVelhos])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.galpao_id) {
      setSubmitError("Selecione o galpao.")
      return
    }
    if (!form.lote_id) {
      setSubmitError("Selecione o plantel. Se nao houver planteis, cadastre um para este galpao primeiro.")
      return
    }
    setSubmitError(null)
    const total = Number(form.total_ovos) || 0
    const quebrados = Math.min(Math.max(0, Number(form.ovos_quebrados_na_coleta) || 0), total)
    if (quebrados > total) {
      setSubmitError("Quebrados na coleta nao pode ser maior que o total.")
      return
    }
    const payload = {
      galpao_id: form.galpao_id,
      lote_id: form.lote_id,
      data: form.data,
      hora: form.hora,
      total_ovos: total,
      ovos_quebrados_na_coleta: quebrados,
    }
    try {
      const res = await apiFetch(`${apiBase}/api/coletas/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (res.ok) {
        const created = await res.json() as Coleta
        const mortalidadeQty = Number(form.mortalidade_quantidade) || 0
        const mortalidadeCausa = (form.mortalidade_causa || "").trim()
        setSubmitError(null)
        setMortalidadePostError(null)
        setShowRetryMortalidade(false)
        setFailedMortalidadeDraft(null)
        setLastCreatedColeta(created)
        setForm({
          galpao_id: galpoes[0]?.id || "",
          lote_id: lotes.find(l => l.galpao_id === galpoes[0]?.id)?.id || "",
          data: todayISODate(),
          hora: `${String(new Date().getHours()).padStart(2, "0")}:${String(new Date().getMinutes()).padStart(2, "0")}`,
          total_ovos: 0,
          ovos_quebrados_na_coleta: 0,
          mortalidade_quantidade: 0,
          mortalidade_causa: "",
        })
        setShowForm(false)
        fetchColetas()
        if (showPendentes) fetchPendentes()
        fetchAlertasPendentesVelhos()

        if (mortalidadeQty > 0) {
          const mRes = await apiFetch(`${apiBase}/api/mortalidades`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              coleta_id: created.id,
              galpao_id: payload.galpao_id,
              lote_id: payload.lote_id,
              data: payload.data,
              quantidade: mortalidadeQty,
              causa: mortalidadeCausa || null,
            }),
          })
          if (!mRes.ok) {
            const detail = await parseApiDetail(mRes)
            setMortalidadePostError(mortalidadeErrorMessage(mRes.status, detail))
            setFailedMortalidadeDraft({ quantidade: mortalidadeQty, causa: mortalidadeCausa })
            setShowRetryMortalidade(true)
          }
        }
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string | { msg?: string }[] }
        const msg = typeof errData?.detail === "string"
          ? errData.detail
          : Array.isArray(errData?.detail) && errData.detail[0]?.msg
            ? errData.detail[0].msg
            : "Erro ao registrar coleta. Tente novamente."
        setSubmitError(msg)
      }
    } catch (e) {
      console.error(e)
      setSubmitError("Erro de conexao. Tente novamente.")
    }
  }

  const formatarData = (dataStr: string) => {
    return toDateInputValue(dataStr) ? formatBRDate(dataStr) : "-"
  }

  const getGalpaoNome = (id: string) => galpoes.find(g => g.id === id)?.nome || "N/A"
  const getLoteCodigo = (id: string) => lotes.find(l => l.id === id)?.codigo || "N/A"

  const statusLabel: Record<string, string> = {
    PENDENTE: "Aguardando classificacao",
    EM_CLASSIFICACAO: "Em classificacao",
    CLASSIFICADA: "Classificada",
  }

  const handleOpenEdit = (c: Coleta) => {
    setEditColeta(c)
    setEditForm({
      total_ovos: c.total_ovos,
      ovos_quebrados_na_coleta: c.ovos_quebrados_na_coleta,
      motivo_alteracao: "",
    })
    setEditError(null)
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editColeta) return
    setEditError(null)
    const total = Number(editForm.total_ovos) || 0
    const quebrados = Math.min(Math.max(0, Number(editForm.ovos_quebrados_na_coleta) || 0), total)
    if (quebrados > total) {
      setEditError("Quebrados nao pode ser maior que o total.")
      return
    }
    if (exigeMotivo(editColeta.total_ovos, total) && !(editForm.motivo_alteracao || "").trim()) {
      setEditError("Alteracao grande: informe o motivo (obrigatorio para diferenca >= 200 ovos ou >= 2%).")
      return
    }
    try {
      const res = await apiFetch(`${apiBase}/api/coletas/${editColeta.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          total_ovos: total,
          ovos_quebrados_na_coleta: quebrados,
          motivo_alteracao: (editForm.motivo_alteracao || "").trim() || null,
          versao_atual: editColeta.versao,
        }),
      })
      if (res.ok) {
        setEditColeta(null)
        fetchColetas()
        if (showPendentes) fetchPendentes()
        fetchAlertasPendentesVelhos()
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string }
        setEditError(typeof errData?.detail === "string" ? errData.detail : "Erro ao salvar.")
      }
    } catch (e) {
      console.error(e)
      setEditError("Erro de conexao.")
    }
  }

  const handleImprimirEtiqueta = async (coletaId: string, _codigo: string) => {
    setPrintError(null)
    setPrintingId(coletaId)
    try {
      const res = await apiFetch(`${apiBase}/api/coletas/${coletaId}/etiqueta`)
      if (!res.ok) {
        throw new Error("Erro ao gerar PDF")
      }
      const contentType = res.headers.get("content-type") ?? ""
      if (!contentType.includes("pdf")) {
        throw new Error("Erro ao gerar PDF")
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      window.open(url, "_blank")
      setTimeout(() => URL.revokeObjectURL(url), 5000)
    } catch (e) {
      console.error(e)
      setPrintError("Nao foi possivel gerar a etiqueta. Tente novamente.")
    } finally {
      setPrintingId(null)
    }
  }

  const formatarIdade = (minutos: number | null | undefined) => {
    if (minutos == null || minutos < 0) return "—"
    if (minutos < 60) return `${minutos} min`
    const h = Math.floor(minutos / 60)
    const m = minutos % 60
    return m ? `${h} h ${m} min` : `${h} h`
  }

  const lotesDoGalpao = form.galpao_id
    ? lotes.filter(l => l.galpao_id === form.galpao_id)
    : lotes

  const closeRegistrarColetaDialog = () => {
    setShowForm(false)
    setSubmitError(null)
    setForm(defaultColetaForm())
  }

  if (isSuperAdmin && !selectedTenantId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Selecione uma granja no menu lateral.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h1 className="text-2xl font-bold">Coletas Diarias</h1>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              setSubmitError(null)
              setShowForm(true)
              const hoje = todayISODate()
              const agora = new Date()
              const h = agora.getHours()
              const m = agora.getMinutes()
              const hora = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
              setForm(prev => ({
                ...prev,
                data: hoje,
                hora,
                galpao_id: galpoes[0]?.id || "",
                lote_id: lotes.find(l => l.galpao_id === galpoes[0]?.id)?.id || "",
              }))
            }}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" /> Nova Coleta
          </Button>
        </div>
      </div>

      {loadError && (
        <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm flex items-center justify-between gap-2">
          <span>{loadError}</span>
          <Button type="button" variant="outline" size="sm" onClick={retryLoad}>
            Tentar novamente
          </Button>
        </div>
      )}

      {printError && (
        <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm flex items-center justify-between gap-2">
          <span>{printError}</span>
          <button type="button" onClick={() => setPrintError(null)} className="underline hover:no-underline">Fechar</button>
        </div>
      )}

      {pendentesMais24h > 0 && (
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-amber-600">⚠️</span>
            <span>Atenção: {pendentesMais24h} coleta(s) há mais de 24h aguardando classificação.</span>
          </div>
          <button 
            onClick={() => { setShowPendentes(true); fetchPendentes() }}
            className="text-amber-700 hover:text-amber-900 underline font-medium"
          >
            Ver fila
          </button>
        </div>
      )}

      {showPendentes && (
        <Card>
          <CardHeader>
            <CardTitle>Fila: aguardando classificacao (FIFO)</CardTitle>
          </CardHeader>
          <CardContent>
            {pendentes.length === 0 ? (
              <p className="text-muted-foreground">Nenhuma coleta pendente.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Codigo</th>
                      <th className="text-left py-2">Data / Hora</th>
                      <th className="text-left py-2">Galpao</th>
                      <th className="text-right py-2">Total</th>
                      <th className="text-right py-2">Quebrados</th>
                      <th className="text-right py-2">Idade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendentes.map((p) => (
                      <tr key={p.id} className="border-b">
                        <td className="font-mono py-2">{p.codigo_coleta}</td>
                        <td className="py-2">{formatarData(p.data)} {toTimeInputValue(p.hora)}</td>
                        <td className="py-2">{getGalpaoNome(p.galpao_id)}</td>
                        <td className="text-right py-2">{p.total_ovos.toLocaleString("pt-BR")}</td>
                        <td className="text-right py-2">{(p.ovos_quebrados_na_coleta ?? 0).toLocaleString("pt-BR")}</td>
                        <td className="text-right py-2 text-muted-foreground">{formatarIdade(p.idade_minutos)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={!!editColeta}
        onOpenChange={(open) => {
          if (!open) {
            setEditColeta(null)
            setEditError(null)
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto gap-0 p-0">
          <DialogTitle className="sr-only">
            {editColeta ? `Editar coleta ${editColeta.codigo_coleta}` : 'Editar coleta'}
          </DialogTitle>
          {editColeta && (
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold pr-10">Editar coleta {editColeta.codigo_coleta}</h2>
            {editError && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm flex items-center justify-between gap-2">
                <span>{editError}</span>
                <button type="button" onClick={() => setEditError(null)} className="underline hover:no-underline">Fechar</button>
              </div>
            )}
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Total coletado</Label>
                  <Input type="number" min={0} value={numberFieldValue(editForm.total_ovos)} placeholder="0" onChange={(e) => setEditForm({ ...editForm, total_ovos: parseCount(e.target.value) })} required />
                </div>
                <div className="space-y-2">
                  <Label>Quebrados (observado na coleta)</Label>
                  <Input type="number" min={0} max={editForm.total_ovos || 0} value={numberFieldValue(editForm.ovos_quebrados_na_coleta)} placeholder="0" onChange={(e) => setEditForm({ ...editForm, ovos_quebrados_na_coleta: parseCount(e.target.value) })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Motivo da alteracao {exigeMotivo(editColeta.total_ovos, editForm.total_ovos) ? "(obrigatorio)" : "(opcional)"}</Label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="Ex.: correcao de contagem"
                  value={editForm.motivo_alteracao}
                  onChange={(e) => setEditForm({ ...editForm, motivo_alteracao: e.target.value })}
                />
              </div>
              {editForm.total_ovos > 0 && editForm.ovos_quebrados_na_coleta > editForm.total_ovos && (
                <p className="text-sm text-amber-600">Quebrados nao pode ser maior que o total.</p>
              )}
              <div className="flex gap-2">
                <Button type="submit">Salvar</Button>
                <Button variant="outline" type="button" onClick={() => { setEditColeta(null); setEditError(null) }}>Cancelar</Button>
              </div>
            </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {lastCreatedColeta && (
        <Card className="max-w-lg border-green-200 bg-green-50 dark:bg-green-950/20">
          <CardHeader>
            <CardTitle className="text-green-800 dark:text-green-200">Coleta registrada</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="font-mono text-lg font-semibold">Codigo da Coleta: {lastCreatedColeta.codigo_coleta}</p>
            {printError && (
              <p className="text-sm text-destructive">{printError}</p>
            )}
            <Button
              variant="outline"
              size="sm"
              title="Gerar PDF da etiqueta"
              disabled={printingId === lastCreatedColeta.id}
              onClick={() => handleImprimirEtiqueta(lastCreatedColeta.id, lastCreatedColeta.codigo_coleta)}
            >
              <Printer className="h-4 w-4 mr-2" /> Imprimir etiqueta
            </Button>
            {mortalidadePostError && (
              <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm space-y-2">
                <p>Coleta registrada, mas a mortalidade não foi lançada: {mortalidadePostError}</p>
                {!showRetryMortalidade && (
                  <Button size="sm" variant="outline" onClick={() => setShowRetryMortalidade(true)}>
                    Tentar lançar a mortalidade de novo
                  </Button>
                )}
              </div>
            )}
            {showRetryMortalidade && lastCreatedColeta && (
              <div className="border rounded-md p-3 bg-background">
                <p className="text-sm font-medium mb-3">Relançar mortalidade vinculada à coleta</p>
                <MortalidadeAvulsoForm
                  galpoes={galpoes}
                  lotes={lotes}
                  initial={{
                    galpao_id: lastCreatedColeta.galpao_id,
                    lote_id: lastCreatedColeta.lote_id,
                    coleta_id: lastCreatedColeta.id,
                    data: toDateInputValue(lastCreatedColeta.data),
                    quantidade: failedMortalidadeDraft?.quantidade,
                    causa: failedMortalidadeDraft?.causa,
                  }}
                  submitLabel="Lançar mortalidade"
                  onCancel={() => setShowRetryMortalidade(false)}
                  onSubmit={async (payload) => {
                    const mRes = await apiFetch(`${apiBase}/api/mortalidades`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify(payload),
                    })
                    if (!mRes.ok) {
                      const detail = await parseApiDetail(mRes)
                      throw new Error(mortalidadeErrorMessage(mRes.status, detail))
                    }
                    setMortalidadePostError(null)
                    setShowRetryMortalidade(false)
                    setFailedMortalidadeDraft(null)
                  }}
                />
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={() => {
              setLastCreatedColeta(null)
              setMortalidadePostError(null)
              setShowRetryMortalidade(false)
            }}>Fechar</Button>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={showForm}
        onOpenChange={(open) => {
          if (!open) closeRegistrarColetaDialog()
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto gap-0 p-0">
          <DialogTitle className="sr-only">Registrar Coleta</DialogTitle>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold pr-10">Registrar Coleta</h2>
            {submitError && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm flex items-center justify-between gap-2">
                <span>{submitError}</span>
                <button type="button" onClick={() => setSubmitError(null)} className="underline hover:no-underline">Fechar</button>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Galpao</Label>
                <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2" value={form.galpao_id} onChange={(e) => setForm({ ...form, galpao_id: e.target.value, lote_id: lotes.find(l => l.galpao_id === e.target.value)?.id || "" })} required>
                  {galpoes.map(g => (
                    <option key={g.id} value={g.id}>{g.nome}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Plantel</Label>
                <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2" value={form.lote_id} onChange={(e) => setForm({ ...form, lote_id: e.target.value })} required>
                  {lotesDoGalpao.map(l => (
                    <option key={l.id} value={l.id}>{l.codigo}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data</Label>
                  <Input type="date" value={toDateInputValue(form.data)} onChange={(e) => setForm({ ...form, data: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Hora</Label>
                  <Input type="time" value={toTimeInputValue(form.hora)} onChange={(e) => setForm({ ...form, hora: toTimeInputValue(e.target.value) })} />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Total coletado (liquido)</Label>
                  <Input type="number" min={0} value={numberFieldValue(form.total_ovos)} placeholder="0" onChange={(e) => setForm({ ...form, total_ovos: parseCount(e.target.value) })} required />
                  <p className="text-xs text-muted-foreground">Ja descontados os trincados, se houver</p>
                </div>
                <div className="space-y-2">
                  <Label>Trincados identificados (info)</Label>
                  <Input type="number" min={0} max={form.total_ovos || 0} value={numberFieldValue(form.ovos_quebrados_na_coleta)} placeholder="0" onChange={(e) => setForm({ ...form, ovos_quebrados_na_coleta: parseCount(e.target.value) })} />
                  <p className="text-xs text-muted-foreground">Apenas para registro, nao afeta o total</p>
                </div>
              </div>
              {form.total_ovos > 0 && form.ovos_quebrados_na_coleta > form.total_ovos && (
                <p className="text-sm text-amber-600">
                  Quebrados nao pode ser maior que o total.
                </p>
              )}
              <div className="rounded-md border border-dashed p-4 space-y-3">
                <p className="text-sm font-medium">Mortalidade (opcional)</p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Quantidade de aves</Label>
                    <Input
                      type="number"
                      min={0}
                      value={numberFieldValue(form.mortalidade_quantidade)}
                      placeholder="0"
                      onChange={(e) => setForm({ ...form, mortalidade_quantidade: parseCount(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Causa (opcional)</Label>
                    <Input
                      value={form.mortalidade_causa}
                      onChange={(e) => setForm({ ...form, mortalidade_causa: e.target.value })}
                      placeholder="Ex.: doença"
                      maxLength={100}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Se informada, a mortalidade será registrada após a coleta e ficará pendente de confirmação do admin.
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="submit">Registrar</Button>
                <Button variant="outline" type="button" onClick={closeRegistrarColetaDialog}>Cancelar</Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid gap-4">
        {coletas.map((c) => (
          <Card key={c.id} className="overflow-hidden border border-gray-200 shadow-sm">
            {/* Header */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-100 flex justify-between items-center">
              <span className="font-mono text-sm font-semibold text-gray-700">{c.codigo_coleta ?? "—"}</span>
              <span className="text-sm text-gray-500">
                {formatarData(c.data)} às {toTimeInputValue(c.hora)}
              </span>
            </div>
            
            {/* Body */}
            <CardContent className="p-4">
              <div className="grid grid-cols-3 gap-4 mb-4">
                {/* Galpão */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-2">
                    <Home className="h-6 w-6 text-gray-500" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{getGalpaoNome(c.galpao_id)}</span>
                </div>
                
                {/* Plantel */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-2">
                    <Tag className="h-6 w-6 text-gray-500" />
                  </div>
                  <span className="text-xs text-gray-500">Plantel:</span>
                  <span className="text-sm font-medium text-gray-700">{getLoteCodigo(c.lote_id)}</span>
                </div>
                
                {/* Ovos */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-2">
                    <Egg className="h-6 w-6 text-gray-500" />
                  </div>
                  <span className="text-2xl font-bold text-gray-800">{c.total_ovos.toLocaleString("pt-BR")}</span>
                  <span className="text-xs text-gray-500">Ovos Totais</span>
                </div>
              </div>
              
              {/* Status e Ações */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${c.status_classificacao === "PENDENTE" ? "bg-amber-400" : c.status_classificacao === "CLASSIFICADA" ? "bg-emerald-500" : "bg-slate-400"}`}></span>
                  <span className="text-sm text-gray-600">Status: {statusLabel[c.status_classificacao] ?? c.status_classificacao}</span>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 hover:bg-gray-100"
                    disabled={c.status_classificacao !== "PENDENTE"}
                    title={c.status_classificacao !== "PENDENTE" ? "Só pode editar quando status é Aguardando classificação" : "Editar"}
                    aria-label={c.status_classificacao !== "PENDENTE" ? "Edição indisponível: coleta já classificada" : `Editar coleta ${c.codigo_coleta}`}
                    onClick={() => handleOpenEdit(c)}
                  >
                    <Pencil className="h-4 w-4 text-gray-500" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 hover:bg-gray-100"
                    title="Gerar PDF da etiqueta"
                    aria-label={`Gerar etiqueta da coleta ${c.codigo_coleta}`}
                    disabled={printingId === c.id}
                    onClick={() => handleImprimirEtiqueta(c.id, c.codigo_coleta)}
                  >
                    <Printer className="h-4 w-4 text-gray-500" />
                  </Button>
                </div>
              </div>
              
              {/* Info adicional */}
              {(c.ovos_quebrados_na_coleta ?? 0) > 0 && (
                <p className="text-xs text-gray-500 mt-2">Trincados: {(c.ovos_quebrados_na_coleta ?? 0).toLocaleString("pt-BR")}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/coletas')({
  component: ColetasPage,
})
